'use client';

import { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { Button } from '@/components/Button';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

const faqs: FAQItem[] = [
  {
    question: 'What is EquityLabs?',
    answer:
      'EquityLabs is a portfolio tracking application that helps you monitor your stock investments with AI-powered insights.',
  },
  {
    question: 'How much does EquityLabs cost?',
    answer:
      'EquityLabs offers a free tier that allows you to track up to 5 assets. For unlimited portfolio tracking, you can upgrade to Premium for $5 CAD/month or $49.99 CAD/year (save ~17%).',
  },
  {
    question: 'Can I cancel my subscription anytime?',
    answer:
      'Yes, you can cancel your Premium subscription at any time through the Stripe billing portal. Your subscription will remain active until the end of your current billing period, then your account will revert to the free tier.',
  },
  {
    question: 'What happens to my assets if I cancel my subscription?',
    answer:
      'When your subscription ends (at the end of your billing period after cancellation), if you have more than 5 assets, your portfolio will be trimmed to the 5 most recently added assets to match the free tier limit.',
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit cards including Visa, Mastercard, and American Express through our secure payment processor, Stripe.',
  },
  {
    question: 'Is there support?',
    answer: (
      <>
        Yes! You can always{' '}
        {process.env.NEXT_PUBLIC_SUPPORT_URL && (
          <>
            <a
              href={process.env.NEXT_PUBLIC_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-accent-muted)]"
            >
              submit feedback here
            </a>
            {' or '}
          </>
        )}
        <a
          href="https://x.com/equity_labs_app"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--color-accent-muted)]"
        >
          DM us on Twitter
        </a>
        .
      </>
    ),
  },
  {
    question:
      "I don't want a subscription but I'd like to show my appreciation. How can I support you?",
    answer: (
      <>
        {"That's so kind of you! If you'd like to support EquityLabs without a subscription, you can "}
        <a
          href="https://buymeacoffee.com/dpang"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--color-accent-muted)]"
        >
          buy me a coffee
        </a>
        . Every contribution is greatly appreciated and helps keep the project going!
      </>
    ),
  },
];

function FAQAccordion({ item }: { item: FAQItem }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-[var(--color-border)] last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left hover:text-[var(--color-text)] transition-colors"
      >
        <span className="font-medium text-[var(--color-text-primary)]">{item.question}</span>
        <ChevronDown
          size={20}
          className={`text-[var(--color-text-secondary)] transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="pb-4 text-[var(--color-text-secondary)] text-sm leading-relaxed">
          {item.answer}
        </div>
      )}
    </div>
  );
}

const faqSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How does AI stock portfolio analysis work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Equity Labs combines market data with AI tools to analyze market trends, news sentiment, and portfolio performance to provide actionable insights.',
      },
    },
    {
      '@type': 'Question',
      name: 'What makes Equity Labs different from other portfolio trackers?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Equity Labs offers the ability to track assets, gain insightful data and trends, and the ability to learn more about the stock markets.',
      },
    },
  ],
});

export default function FAQPage() {
  return (
    <div className="container mx-auto p-4 lg:p-8 max-w-3xl">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: faqSchema }}
      />
      <Link href="/">
        <Button variant="ghost" size="sm" className="mb-6">
          <ArrowLeft size={16} className="mr-2" /> Back
        </Button>
      </Link>

      <h1 className="text-3xl font-bold text-[var(--color-text)] mb-2">
        Frequently Asked Questions
      </h1>
      <p className="text-[var(--color-text-secondary)] mb-8">
        Find answers to common questions about EquityLabs.
      </p>

      <div className="rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)] p-6">
        {faqs.map((faq, index) => (
          <FAQAccordion key={index} item={faq} />
        ))}
      </div>

      {process.env.NEXT_PUBLIC_SUPPORT_URL && (
        <div className="mt-8 text-center">
          <p className="text-[var(--color-text-secondary)] mb-4">Still have questions?</p>
          <a
            href={process.env.NEXT_PUBLIC_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--color-accent-muted)] transition-colors"
          >
            Contact Support
          </a>
        </div>
      )}
    </div>
  );
}
